'use client';

import { useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';

/**
 * Keep the httpOnly access cookie (`mnrq-at`) fresh (09a #4).
 *
 * Web sign-in goes through the client SDK, which owns the legacy session and
 * rotates its refresh token on its own schedule. The server access cookie is
 * only issued by server routes, and nothing used to re-issue it — so after
 * the access token's hour expired, every request carried a dead `mnrq-at`.
 * This hook closes that gap WITHOUT touching refresh tokens: it asks
 * `POST /api/auth/session/renew` to mirror the browser's current access
 * token into the access cookie. No rotation, so no clash with the SDK.
 *
 * Triggers: immediately on mount (covers post-sign-in), on every SDK token
 * refresh, when the tab becomes visible again, and on a 20-minute interval
 * (the access token lives an hour; three mirrors per lifetime leaves slack
 * for a laptop that slept through one). Fire-and-forget: a failed mirror
 * leaves the session exactly as it was, and the next trigger retries.
 */

const RENEW_INTERVAL_MS = 20 * 60 * 1000;
const VISIBILITY_DEBOUNCE_MS = 2_000;

export function useSessionRenewal(enabled: boolean): void {
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    const renew = async () => {
      if (inFlightRef.current) return;
      if (typeof navigator !== 'undefined' && !navigator.onLine) return;
      inFlightRef.current = true;
      try {
        await fetch('/api/auth/session/renew', { method: 'POST' });
      } catch {
        // Best-effort: the next trigger retries. A failed mirror must never
        // disturb the session — that is the whole point of mirroring instead
        // of refreshing.
      } finally {
        inFlightRef.current = false;
      }
      if (cancelled) return;
    };

    void renew();

    const { data: listener } = supabase.auth.onAuthStateChange((event) => {
      if (cancelled) return;
      if (event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN') {
        void renew();
      }
    });

    let visibilityTimer: ReturnType<typeof setTimeout> | null = null;
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (visibilityTimer) clearTimeout(visibilityTimer);
      visibilityTimer = setTimeout(() => {
        if (!cancelled) void renew();
      }, VISIBILITY_DEBOUNCE_MS);
    };
    document.addEventListener('visibilitychange', handleVisibility);

    const interval = setInterval(() => {
      if (!cancelled) void renew();
    }, RENEW_INTERVAL_MS);

    return () => {
      cancelled = true;
      listener.subscription.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibility);
      if (visibilityTimer) clearTimeout(visibilityTimer);
      clearInterval(interval);
    };
  }, [enabled]);
}
